import { message } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { BaseFormData, Report } from 'features/reporting/types'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { useCallback, useMemo } from 'react'

interface UsePdfManagementProps {
  metadataEntryId: string
  formData: BaseFormData
  report: Report
  downloadFilename?: string
}

interface UsePdfManagementResult {
  latestDraftTimestamp?: string
  publishLatestDraftPDF: (pdfPath?: string) => Promise<void>
  handleDownloadLatestDraft: () => Promise<void>
  handleDownloadPublishedPdf: () => Promise<void>
  forceRefresh: () => Promise<void>
}

dayjs.extend(utc)

export const usePdfManagement = ({
  metadataEntryId,
  formData,
  downloadFilename,
}: UsePdfManagementProps): UsePdfManagementResult => {
  const { rolodexUploadFile, rolodexPatch } = useRolodexEdit()

  // Get file references and URLs
  const { fileRefs: pdfRefs, forceRefresh: forceRefreshPdfRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'reportPDFDrafts/**', // All files in reportPDFDrafts directory
    })
  // Get published PDF URL
  const {
    results: publishedPdfUrls,
    forceRefresh: forceRefreshPublishedPdfUrls,
  } = useRolodexSignedURLs(
    formData.publishedPdfPath
      ? [
          {
            entry_id: metadataEntryId,
            path: formData.publishedPdfPath,
          },
        ]
      : []
  )

  const { latestDraftRef, latestDraftTimestamp } = useMemo(() => {
    const timestampRegex = /draft-(\d{8}-\d{6})\.pdf$/

    const sortedRefs = [...pdfRefs].sort((a, b) => {
      const timestampA = a.path.match(timestampRegex)?.[1]
      const timestampB = b.path.match(timestampRegex)?.[1]

      if (!timestampA || !timestampB) return 0

      return timestampB.localeCompare(timestampA) // Descending order (latest first)
    })

    const latestDraft = sortedRefs[0]
    if (!latestDraft)
      return { latestDraftRef: undefined, latestDraftTimestamp: undefined }

    const match = latestDraft.path.match(timestampRegex)
    if (!match)
      return { latestDraftRef: undefined, latestDraftTimestamp: undefined }

    const isoString = match[1].replace(
      /(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/,
      '$1-$2-$3T$4:$5:$6Z'
    )
    const latestDraftTimestamp = dayjs
      .utc(isoString)
      .format('MMM DD, YYYY HH:mm:ss [UTC]')

    return { latestDraftRef: latestDraft, latestDraftTimestamp }
  }, [pdfRefs])

  const { url: latestDraftUrl, forceRefresh: forceRefreshLatestDraftUrl } =
    useRolodexSignedURLs(
      latestDraftRef
        ? [
            {
              entry_id: latestDraftRef?.entry_id,
              path: latestDraftRef?.path,
            },
          ]
        : []
    )

  const handleDownloadLatestDraft = useCallback(async (): Promise<void> => {
    if (!latestDraftUrl) {
      message.error('No draft PDF available')
      return
    }

    try {
      const response = await fetch(latestDraftUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadFilename
        ? `${downloadFilename}_Draft.pdf`
        : 'inspection-report-draft.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download draft PDF:', error)
      message.error('Failed to download draft PDF')
    }
  }, [latestDraftUrl, downloadFilename])

  const publishLatestDraftPDF = useCallback(
    async (pdfPath?: string): Promise<void> => {
      if (!latestDraftUrl) {
        message.error('No draft PDF available')
        return
      }

      try {
        const response = await fetch(latestDraftUrl)
        const blob = await response.blob()

        const publishedPath =
          pdfPath ||
          formData.publishedPdfPath ||
          `reportPDFs/report-published.pdf`

        await rolodexUploadFile(metadataEntryId, blob, publishedPath)

        if (publishedPath !== formData.publishedPdfPath) {
          await rolodexPatch([
            {
              id: metadataEntryId,
              data: {
                published_pdf_path: publishedPath,
              },
            },
          ])
        }

        message.success('PDF published successfully')
      } catch (error) {
        console.error('Failed to publish draft PDF:', error)
        message.error('Failed to publish draft PDF')
      }
    },
    [
      formData.publishedPdfPath,
      metadataEntryId,
      latestDraftUrl,
      rolodexUploadFile,
      rolodexPatch,
    ]
  )

  const handleDownloadPublishedPdf = useCallback(async (): Promise<void> => {
    if (!formData.publishedPdfPath) {
      message.error('No published PDF available')
      return
    }

    const publishedUrl = publishedPdfUrls[0]?.signed_url
    if (!publishedUrl) {
      message.error('Failed to get published PDF URL')
      return
    }

    try {
      const response = await fetch(publishedUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadFilename
        ? `${downloadFilename}.pdf`
        : 'inspection-report-published.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download published PDF:', error)
      message.error('Failed to download published PDF')
    }
  }, [formData.publishedPdfPath, publishedPdfUrls, downloadFilename])

  const forceRefresh = useCallback(async () => {
    await forceRefreshPdfRefs()
    await forceRefreshLatestDraftUrl()
    await forceRefreshPublishedPdfUrls()
  }, [
    forceRefreshPdfRefs,
    forceRefreshLatestDraftUrl,
    forceRefreshPublishedPdfUrls,
  ])

  return {
    latestDraftTimestamp,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    forceRefresh,
  }
}
